import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMin, fmtTime, fmtDT } from "@/lib/format";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Truck, PackageCheck, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { OrderTimeline } from "@/components/order/OrderTimeline";
import { OrderTasksTable } from "@/components/order/OrderTasksTable";
import { OrderAuditTable } from "@/components/order/OrderAuditTable";
import { OrderV2Sections, StageSlaCard } from "@/components/order/OrderV2Sections";
import { OrderDialogs } from "@/components/order/OrderDialogs";
import { OwnerBanner, WorkflowPanels } from "@/components/order/WorkflowPanels";
import { canCreateShipment } from "@/components/order/orderLineUtils";

const CHECKLIST_LABELS = {
  correct_customer: "Correct customer", correct_invoice: "Correct invoice", correct_product: "Correct product",
  correct_quantity: "Correct quantity", correct_packing: "Correct packing", correct_address: "Correct shipping address",
  qc_approved: "QC approved", documents_complete: "Documents complete", vehicle_recorded: "Vehicle details recorded",
  loading_completed: "Loading completed",
};

const MGR = ["admin", "warehouse_manager", "supervisor"];
const DOCS_ROLES = ["admin", "warehouse_manager", "accounts"];
const DSP_ROLES = ["admin", "warehouse_manager", "supervisor", "dispatch", "dispatch_manager"];
const PROD_ROLES = ["admin", "warehouse_manager", "production"];

export default function OrderDetail() {
  const { billNo } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [dialog, setDialog] = useState(null); // docs | vehicle | dispatch | pod | prod | sendback
  const [docsForm, setDocsForm] = useState({});
  const [vehicleForm, setVehicleForm] = useState({ vehicle_number: "", driver_name: "", driver_mobile: "", transporter: "", vehicle_type: "", loading_bay: "" });
  const [dispatchForm, setDispatchForm] = useState({ lr_number: "", eway_bill: "", transporter: "", dispatch_person: "" });
  const [podForm, setPodForm] = useState({ remarks: "", shortage: "", damage: "" });
  const [prodQty, setProdQty] = useState("");
  const [sendbackRemarks, setSendbackRemarks] = useState("");
  const [checks, setChecks] = useState({});
  const [shipOpen, setShipOpen] = useState(false);
  const [shipForm, setShipForm] = useState({ vehicle_number: "", driver_name: "", transporter: "", lr_number: "", invoice_no: "" });
  const [shipQtys, setShipQtys] = useState({});

  const load = useCallback(async () => {
    const res = await api.get(`/orders/v2/${billNo}`);
    setOrder(res.data);
    const cl = {};
    Object.entries(CHECKLIST_LABELS).forEach(([k]) => { cl[k] = !!(res.data.dispatch_checklist?.[k]?.done); });
    setChecks(cl);
    setDocsForm({
      invoice_no: res.data.documents?.invoice_no || "", eway_bill: res.data.documents?.eway_bill || "",
      delivery_challan: res.data.documents?.delivery_challan || "", packing_list: !!res.data.documents?.packing_list,
      lr_number: res.data.documents?.lr_number || "", transporter: res.data.documents?.transporter || "",
    });
  }, [billNo]);

  useEffect(() => { load().catch(() => setOrder(null)); }, [load]);

  const act = async (path, body = {}) => {
    try {
      await api.post(`/orders/${billNo}/${path}`, body);
      toast.success("Recorded");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed");
    }
  };

  const actV2 = async (path, body = {}) => {
    try {
      await api.post(`/orders/v2/${billNo}/${path}`, body);
      toast.success("Recorded");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed");
    }
  };

  const createShipment = async () => {
    const lines = Object.entries(shipQtys)
      .filter(([, q]) => parseInt(q) > 0)
      .map(([lineKey, q]) => {
        const line = (order.items || []).find((i) => String(i.line_no ?? i.sku) === lineKey);
        return line ? { sku: line.sku, line_no: line.line_no, quantity: parseInt(q) } : null;
      })
      .filter(Boolean);
    if (!lines.length) return toast.error("Enter quantity for at least one SKU");
    if (!shipForm.vehicle_number || !shipForm.driver_name) return toast.error("Vehicle number and driver are required");
    await actV2("shipments", { ...shipForm, transporter: shipForm.transporter || null, lr_number: shipForm.lr_number || null, invoice_no: shipForm.invoice_no || null, lines });
    setShipOpen(false);
    setShipForm({ vehicle_number: "", driver_name: "", transporter: "", lr_number: "", invoice_no: "" });
    setShipQtys({});
  };


  const openShipment = () => {
    setShipQtys({});
    setShipForm({ vehicle_number: "", driver_name: "", transporter: "", lr_number: "", invoice_no: "" });
    setShipOpen(true);
  };

  if (!order) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const fs = order.fulfilment_status;
  const st = order.stages || {};
  const isV2 = !!(order.items && order.items.length);
  const role = user?.role;
  const can = (roles) => roles.includes(role);
  const stock = order.stock_live;
  const checklistDone = Object.values(checks).every(Boolean);
  const loadingDone = !!(st.loading?.end || order.outward?.loading_end);

  const nextPod = { dispatched: "in_transit", in_transit: "delivered", delivered: "pod_received", pod_received: "closed" }[fs];

  const shipmentAllowed = isV2 && canCreateShipment(order, fs);
  const v2Shipments = isV2 ? (order.shipments || []).filter((s) => s.status !== "cancelled") : [];
  const latestV2Shipment = [...v2Shipments].reverse().find((s) => ["dispatched", "in_transit", "delivered", "pod_received"].includes(s.status));
  const v2Dispatched = v2Shipments.some((s) => ["dispatched", "in_transit", "delivered", "pod_received"].includes(s.status));
  const v2PodComplete = v2Shipments.length > 0 && v2Shipments.every((s) => s.status === "pod_received");
  const documentLrs = order.documents?.lrs || [];
  const documentLrText = documentLrs.length
    ? documentLrs.map((lr) => `${lr.shipment_id ? `${lr.shipment_id}: ` : ""}${lr.lr_number}`).join(" · ")
    : order.documents?.lr_number || "—";
  const hasActions =
    (!isV2 && ["order_received", "verification_pending"].includes(fs) && can(MGR)) ||
    (!isV2 && fs === "stock_check_pending" && can(MGR)) ||
    (!isV2 && fs === "stock_available" && can(MGR)) ||
    fs === "stock_short" ||
    (fs === "picking_completed" && can(MGR)) ||
    ["picking_pending", "picking_in_progress", "qc_pending", "packing_pending", "packing_in_progress", "loading_in_progress"].includes(fs) ||
    (!isV2 && fs === "documentation_pending" && can(DOCS_ROLES)) ||
    (!isV2 && fs === "ready_for_dispatch" && can(DSP_ROLES) && !order.vehicle) ||
    (!isV2 && fs === "loading_completed" && can(DSP_ROLES)) ||
    (!isV2 && nextPod && can(DSP_ROLES)) ||
    (isV2 && order.sales_status === "draft" && can(["admin", "warehouse_manager", "sales"])) ||
    (isV2 && order.sales_status === "submitted" && can(MGR)) ||
    (isV2 && order.sales_status === "approved" && ["stock_available", "partially_available", "stock_short"].includes(fs) && can(MGR)) ||
    (isV2 && fs === "documentation_pending" && can(DOCS_ROLES)) ||
    (isV2 && shipmentAllowed && can(DSP_ROLES));

  return (
    <div className="space-y-5 max-w-5xl" data-testid="order-detail-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/orders")} data-testid="order-back-button"><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight metric" data-testid="order-detail-number">{order.bill_no}</h1>
            <StatusBadge status={fs} />
            {order.priority !== "normal" && <span className={`text-[10px] font-mono uppercase tracking-widest ${order.priority === "urgent" ? "text-signal-red" : "text-signal-amber"}`}>{order.priority}</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {order.customer} · {order.item_name} ({order.sku}) · <span className="metric">{order.quantity.toLocaleString()}</span> pcs
            {order.required_date && <> · required by <span className="metric">{new Date(order.required_date).toLocaleDateString([], { day: "2-digit", month: "short" })}</span></>}
          </p>
        </div>
      </div>

      {order.price_hold && (
        <div className="bg-signal-red/10 border border-signal-red/50 rounded-lg p-4" data-testid="price-hold-banner">
          <p className="text-signal-red font-bold text-sm uppercase tracking-wide">Price Hold — Admin/MD Approval Required</p>
          <p className="text-xs text-muted-foreground mt-1">
            {(order.price_violations || []).map((v) => `${v.sku} (${v.flags.map((f) => f.replace(/_/g, " ")).join(", ")})`).join(" · ")}.
            This order cannot be approved or invoiced until cleared under Products → Price Approvals.
          </p>
        </div>
      )}

      {/* Stock snapshot */}
      {!isV2 && (
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          ["Required", order.quantity],
          ["Physical Stock", stock?.physical],
          ["Reserved (all)", stock?.reserved],
          ["Free Stock", stock?.free],
          ["Shortage", Math.max(0, order.quantity - (stock?.free ?? 0) - (order.reservations?.filter((r) => r.status === "active").reduce((a, r) => a + r.quantity, 0) || 0))],
        ].map(([label, v], i) => (
          <div key={label} className={`bg-card border rounded-lg p-3 ${i === 4 && v > 0 && !["dispatched", "in_transit", "delivered", "pod_received", "closed"].includes(fs) ? "border-signal-red/60" : "border-border"}`} data-testid={`stock-${label.toLowerCase().replace(/\W+/g, "-")}`}>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
            <p className={`metric text-xl font-extrabold mt-0.5 ${i === 4 && v > 0 ? "text-signal-red" : ""}`}>{v != null ? v.toLocaleString() : "—"}</p>
          </div>
        ))}
      </div>
      )}

      {/* Action panel */}
      {hasActions && (
      <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="order-actions-panel">
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Next actions</p>
        <div className="flex flex-wrap gap-2">
          {isV2 && order.sales_status === "draft" && can(["admin", "warehouse_manager", "sales"]) && (
            <>
              <Button variant="secondary" onClick={() => navigate(`/orders/new?edit=${order.bill_no}`)} data-testid="action-edit-draft">Edit Draft</Button>
              <Button className="bg-signal-blue hover:bg-signal-blue/90 text-white" onClick={() => actV2("submit")} data-testid="action-submit-order">Submit for Approval</Button>
            </>
          )}
          {isV2 && order.sales_status === "submitted" && can(MGR) && (
            <Button className="bg-signal-green hover:bg-signal-green/90 text-white" onClick={() => actV2("approve")} data-testid="action-approve-order">Approve Order + Auto Stock Check</Button>
          )}
          {isV2 && order.sales_status === "submitted" && !can(MGR) && (
            <p className="text-sm text-muted-foreground">Submitted — waiting for manager approval.</p>
          )}
          {isV2 && order.sales_status === "approved" && ["stock_available", "partially_available", "stock_short"].includes(fs) && can(MGR) && (
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => actV2("reserve")} data-testid="action-reserve-v2">Reserve Stock (SKU-wise) & Create Picking Tasks</Button>
          )}
          {isV2 && fs === "documentation_pending" && can(DOCS_ROLES) && (
            <Button className="bg-signal-blue hover:bg-signal-blue/90 text-white" onClick={() => actV2("invoices")} data-testid="action-generate-invoice"><FileText className="w-4 h-4 mr-1" /> Generate Invoice</Button>
          )}
          {isV2 && shipmentAllowed && can(DSP_ROLES) && (
            <Button className="bg-signal-orange hover:bg-signal-orange/90 text-white" onClick={openShipment} data-testid="action-new-shipment"><Truck className="w-4 h-4 mr-1" /> New Shipment</Button>
          )}
          {!isV2 && ["order_received", "verification_pending"].includes(fs) && can(MGR) && (
            <Button className="bg-signal-blue hover:bg-signal-blue/90 text-white" onClick={() => act("verify")} data-testid="action-verify-order"><ShieldCheck className="w-4 h-4 mr-1" /> Verify Order</Button>
          )}
          {!isV2 && fs === "stock_check_pending" && can(MGR) && (
            <Button className="bg-signal-blue hover:bg-signal-blue/90 text-white" onClick={() => act("stock-check")} data-testid="action-stock-check"><PackageCheck className="w-4 h-4 mr-1" /> Run Stock Check</Button>
          )}
          {!isV2 && fs === "stock_available" && can(MGR) && (
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => act("reserve")} data-testid="action-reserve-stock">Reserve Stock & Create Picking Task</Button>
          )}
          {fs === "stock_short" && (
            <>
              <span className="text-sm text-signal-red font-medium flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> PRODUCTION REQUIRED — {order.stock?.shortage?.toLocaleString() ?? "…"} pcs</span>
              {can(PROD_ROLES) && (
                <Button variant="outline" onClick={() => { setProdQty(String(order.stock?.shortage || "")); setDialog("prod"); }} data-testid="action-production-received">Mark FG Received</Button>
              )}
            </>
          )}
          {fs === "picking_completed" && can(MGR) && (
            <>
              <Button className="bg-signal-green hover:bg-signal-green/90 text-white" onClick={() => act("verify-picking", { action: "approve" })} data-testid="action-approve-picking">Approve Picking</Button>
              <Button variant="outline" className="border-signal-red/40 text-signal-red" onClick={() => setDialog("sendback")} data-testid="action-sendback-picking">Send Back for Correction</Button>
            </>
          )}
          {["picking_pending", "picking_in_progress", "qc_pending", "packing_pending", "packing_in_progress", "loading_in_progress"].includes(fs) && (
            <p className="text-sm text-muted-foreground">Task active — track it on the <button className="text-signal-blue underline" onClick={() => navigate("/tasks")} data-testid="action-goto-tasks">Tasks page</button>.</p>
          )}
          {!isV2 && fs === "documentation_pending" && can(DOCS_ROLES) && (
            <Button className="bg-signal-blue hover:bg-signal-blue/90 text-white" onClick={() => setDialog("docs")} data-testid="action-documents"><FileText className="w-4 h-4 mr-1" /> Update Documents</Button>
          )}
          {!isV2 && fs === "ready_for_dispatch" && can(DSP_ROLES) && !order.vehicle && (
            <Button className="bg-signal-orange hover:bg-signal-orange/90 text-white" onClick={() => setDialog("vehicle")} data-testid="action-vehicle-checkin"><Truck className="w-4 h-4 mr-1" /> Vehicle Check-in</Button>
          )}
          {!isV2 && fs === "loading_completed" && can(DSP_ROLES) && (
            <Button className="bg-signal-green hover:bg-signal-green/90 text-white" onClick={() => setDialog("dispatch")} data-testid="action-confirm-dispatch">Final Checklist & Confirm Dispatch</Button>
          )}
          {!isV2 && nextPod && can(DSP_ROLES) && (
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setDialog("pod")} data-testid="action-pod-update">
              Mark {nextPod.replace(/_/g, " ").toUpperCase()}
            </Button>
          )}
        </div>
      </div>
      )}

      {/* Documents + vehicle + dispatch info */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-lg p-4" data-testid="order-documents-card">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Documentation</p>
          {order.documents?.complete ? (
            <div className="text-sm space-y-1">
              <p className="text-signal-green font-semibold">Complete</p>
              <p className="metric text-xs">Invoice: {order.documents.invoice_no || "—"}</p>
              <p className="metric text-xs">E-way: {order.documents.eway_bill || "—"} · LRs: {documentLrText}</p>
            </div>
          ) : <p className="text-sm text-signal-red font-semibold">Pending</p>}
        </div>
        <div className="bg-card border border-border rounded-lg p-4" data-testid="order-vehicle-card">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{isV2 && order.delivery_mode === "customer_pickup" ? "Vehicle / Handover" : "Vehicle"}</p>
          {order.vehicle ? (
            <div className="text-sm space-y-1">
              <p className="metric font-bold">{order.vehicle.vehicle_number}</p>
              <p className="text-xs text-muted-foreground">{order.vehicle.driver_name} · {order.vehicle.transporter || "—"}</p>
              {order.outward?.vehicle_arrival && <p className="metric text-xs">arrived {fmtTime(order.outward.vehicle_arrival)}{order.outward.vehicle_waiting_minutes != null && ` · waited ${fmtMin(order.outward.vehicle_waiting_minutes)}`}</p>}
            </div>
          ) : order.pickup ? (
            <div className="text-sm space-y-1">
              <p className="text-signal-green font-semibold">Customer pickup handed over</p>
              <p className="text-xs text-muted-foreground">Received by {order.pickup.receiver_name}</p>
            </div>
          ) : <p className="text-sm text-muted-foreground">Not checked in</p>}
        </div>
        <div className="bg-card border border-border rounded-lg p-4" data-testid="order-dispatch-card">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Dispatch / POD</p>
          {isV2 ? (
            v2Dispatched || order.pickup ? (
              <div className="text-sm space-y-1">
                <p className="metric text-xs">{latestV2Shipment ? `${latestV2Shipment.shipment_id} · ${latestV2Shipment.status.replace(/_/g, " ")}` : "Customer pickup delivered"}</p>
                {latestV2Shipment?.dispatched_at && <p className="text-xs text-muted-foreground">Dispatched {fmtDT(latestV2Shipment.dispatched_at)}</p>}
                <p className="text-xs text-muted-foreground">{latestV2Shipment ? latestV2Shipment.vehicle_number : `Received by ${order.pickup?.receiver_name || "customer"}`}</p>
                {(v2PodComplete || order.pod?.at) && <p className="text-xs text-signal-green">POD received {fmtDT(order.pod?.at || latestV2Shipment?.pod_at)}</p>}
              </div>
            ) : <p className="text-sm text-muted-foreground">Not dispatched</p>
          ) : order.dispatch ? (
            <div className="text-sm space-y-1">
              <p className="metric text-xs">Dispatched {fmtDT(order.stages?.dispatch?.at)}</p>
              <p className="text-xs text-muted-foreground">by {order.dispatch.supervisor} · {order.dispatch.vehicle}</p>
              {order.pod?.pod_received_at && <p className="text-xs text-signal-green">POD received {fmtDT(order.pod.pod_received_at)}</p>}
            </div>
          ) : <p className="text-sm text-muted-foreground">Not dispatched</p>}
        </div>
      </div>

      {/* Exceptions */}
      {order.exceptions?.length > 0 && (
        <div className="bg-card border border-signal-red/40 rounded-lg p-4" data-testid="order-exceptions">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Exceptions</p>
          {order.exceptions.map((e) => (
            <p key={e.exception_id} className="text-sm flex gap-2 flex-wrap">
              <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${e.status === "open" ? "bg-signal-red/15 text-signal-red" : "bg-signal-green/15 text-signal-green"}`}>{e.status}</span>
              <span className="capitalize font-medium">{e.type.replace(/_/g, " ")}</span>
              <span className="text-muted-foreground">{e.reason} · owner: {e.owner}</span>
            </p>
          ))}
        </div>
      )}

      {isV2 && (
        <>
          <OwnerBanner owner={order.current_owner} />
          <WorkflowPanels order={order} fs={fs} viewerRole={user?.role}
            isManager={["admin", "warehouse_manager"].includes(user?.role)}
            isSales={["admin", "sales"].includes(user?.role)} onChanged={load} />
          <OrderV2Sections order={order} fs={fs} can={can} DSP_ROLES={DSP_ROLES} DOCS_ROLES={DOCS_ROLES}
            actV2={actV2} openShipment={openShipment} onChanged={load} />
        </>
      )}

      <StageSlaCard stages={order.stage_sla} />

      <OrderTimeline stages={st} />

      <OrderTasksTable tasks={order.tasks} />

      <OrderAuditTable entries={order.audit_trail} />

      <OrderDialogs
        dialog={dialog} closeDialog={() => setDialog(null)} order={order} act={act} checklistLabels={CHECKLIST_LABELS}
        docsForm={docsForm} setDocsForm={setDocsForm} vehicleForm={vehicleForm} setVehicleForm={setVehicleForm}
        dispatchForm={dispatchForm} setDispatchForm={setDispatchForm} checks={checks} setChecks={setChecks}
        checklistDone={checklistDone} nextPod={nextPod} podForm={podForm} setPodForm={setPodForm}
        prodQty={prodQty} setProdQty={setProdQty} sendbackRemarks={sendbackRemarks} setSendbackRemarks={setSendbackRemarks}
        shipOpen={shipOpen} setShipOpen={setShipOpen} shipForm={shipForm} setShipForm={setShipForm}
        shipQtys={shipQtys} setShipQtys={setShipQtys} createShipment={createShipment}
      />
    </div>
  );
}
